import { useState } from "react";
import { Switch } from "@headlessui/react";
import classNames from "classnames";

import { NotificationSettingsInterest } from "custom-types/User";
import { usePushSubscription } from "hooks/usePushSubscription";

export default function NotificationSettings({ className = "" }) {
  return (
    <div className={className}>
      <h3 className="heading--m mb-lg">Browser Push Notifications</h3>
      <Settings />
    </div>
  );
}

const Settings = () => {
  const {
    followTopic,
    loading,
    permission,
    settings,
    supportsPush,
    unfollowTopic,
  } = usePushSubscription();

  const [checkboxDisabled, setCheckboxDisabled] = useState(false);

  const checkboxOnChange = async (checked: boolean, subject: string) => {
    setCheckboxDisabled(true);

    if (checked) {
      await followTopic(subject);
    } else {
      await unfollowTopic(subject);
    }

    setCheckboxDisabled(false);
  };

  if (loading) {
    return <>Loading…</>;
  } else if (!supportsPush) {
    return (
      <div className="rounded bg-green-20 p-sm text-xs">
        We don’t support sending push notifications to your device at this time.
      </div>
    );
  } else if (permission === "denied") {
    return (
      <div className="rounded bg-error-20 p-sm text-xs">
        You’ve turned off push notifications in this browser. Re-enable them in
        your browser settings to use this functionality.
      </div>
    );
  } else {
    return (
      <div className="mb-xl">
        {settings?.interests?.map(
          (
            interest: NotificationSettingsInterest,
            i: number,
            is: NotificationSettingsInterest[],
          ) => (
            <Switch.Group key={interest.subject}>
              <div
                className={classNames("flex justify-between", {
                  "border-b border-light-grey pb-md mb-md": i !== is.length - 1,
                })}
              >
                <Switch.Label className="pr-sm">
                  <div className="font-bold">{interest.title}</div>
                  <div className="text-grey text-xs">
                    {interest.description}
                  </div>
                </Switch.Label>
                <Switch
                  checked={interest.enabled}
                  disabled={checkboxDisabled}
                  onChange={(checked: boolean) =>
                    checkboxOnChange(checked, interest.subject)
                  }
                  className={classNames(
                    interest.enabled ? "bg-default" : "bg-grey-44",
                    "relative inline-flex flex-shrink-0 h-6 w-10 border-2 border-transparent rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500",
                    { "cursor-default": checkboxDisabled },
                  )}
                >
                  <span className="sr-only">{`Toggle ${interest.title} notifications`}</span>
                  <span
                    aria-hidden="true"
                    className={classNames(
                      interest.enabled ? "translate-x-4" : "translate-x-0",
                      "pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition",
                    )}
                  />
                </Switch>
              </div>
            </Switch.Group>
          ),
        )}
      </div>
    );
  }
};
